import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import {
  resolveSingleScaleYAxisRange,
  resolveYAxisRange,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { SensorAxisMapping } from 'types'

const LABEL_HORIZONTAL_OFFSET = 36
export const SINGLE_SCALE_AXIS_ID = 'single-scale'

type SensorToRender = {
  id: string
  name: string
}

type SensorRange = {
  min?: number
  max?: number
  unit: string
}

export interface VisibleSensorToRender extends SensorToRender {
  originalIndex: number
}

export interface YAxisOverlayLabel {
  axisId: string
  key: string
  numericValue: number
  value: string
  position: 'top' | 'bottom'
  side: 'left' | 'right'
  offset: number
  color: string
}

interface BuildYAxisOverlayLabelsArgs {
  visibleSensorsToRender: VisibleSensorToRender[]
  singleScaleMode: boolean
  sensorRanges: Record<string, SensorRange>
  customAxisRanges: Record<string, { min?: number; max?: number }>
  sensorAxisMapping: SensorAxisMapping
  customSensorColors: Record<string, string>
}

const formatYAxisOverlayValue = (value: number) =>
  Math.abs(value) >= 1000 || (Math.abs(value) < 0.01 && value !== 0)
    ? value.toExponential(1)
    : value.toFixed(1)

export const getVisibleSensorsToRender = (
  allSensorsToRender: SensorToRender[],
  sensorVisibilityConfig: Record<string, boolean>
): VisibleSensorToRender[] =>
  allSensorsToRender.flatMap((sensor, originalIndex) =>
    sensorVisibilityConfig[sensor.id] === false
      ? []
      : [{ ...sensor, originalIndex }]
  )

export const buildYAxisOverlayLabels = ({
  visibleSensorsToRender,
  singleScaleMode,
  sensorRanges,
  customAxisRanges,
  sensorAxisMapping,
  customSensorColors,
}: BuildYAxisOverlayLabelsArgs): YAxisOverlayLabel[] => {
  if (visibleSensorsToRender.length === 0) return []

  if (singleScaleMode) {
    const { min, max } = resolveSingleScaleYAxisRange(
      visibleSensorsToRender,
      sensorRanges,
      customAxisRanges['single-scale']
    )

    return [
      {
        axisId: SINGLE_SCALE_AXIS_ID,
        key: 'single-max',
        numericValue: max,
        value: formatYAxisOverlayValue(max),
        position: 'top',
        side: 'left',
        offset: 0,
        color: '#ccc',
      },
      {
        axisId: SINGLE_SCALE_AXIS_ID,
        key: 'single-min',
        numericValue: min,
        value: formatYAxisOverlayValue(min),
        position: 'bottom',
        side: 'left',
        offset: 0,
        color: '#ccc',
      },
    ]
  }

  const labels: YAxisOverlayLabel[] = []
  let leftOffset = 0
  let rightOffset = 0

  visibleSensorsToRender.forEach(({ id, originalIndex }) => {
    const axisInfo = sensorAxisMapping[id]
    if (!axisInfo) return

    const color = customSensorColors[id] || getSensorColor(originalIndex)
    const side = axisInfo.side
    const { min, max } = resolveYAxisRange(
      sensorRanges[id],
      customAxisRanges[id]
    )

    const offset = side === 'right' ? rightOffset : leftOffset

    labels.push(
      {
        axisId: id,
        key: `${id}-max`,
        numericValue: max,
        value: formatYAxisOverlayValue(max),
        position: 'top',
        side,
        offset,
        color,
      },
      {
        axisId: id,
        key: `${id}-min`,
        numericValue: min,
        value: formatYAxisOverlayValue(min),
        position: 'bottom',
        side,
        offset,
        color,
      }
    )

    if (side === 'right') {
      rightOffset += LABEL_HORIZONTAL_OFFSET
    } else {
      leftOffset += LABEL_HORIZONTAL_OFFSET
    }
  })

  return labels
}
